/**
 * @fileoverview Authentication store for Firebase Auth integration
 * @module stores/auth
 */
import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import {
  type User,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  OAuthProvider,
  signOut as firebaseSignOut,
  createUserWithEmailAndPassword
} from 'firebase/auth'
import { auth } from '@/plugins/firebase'
import type { UserCustomClaims } from '@/types'

/**
 * Authentication store managing user state and Firebase Auth
 */
export const useAuthStore = defineStore('auth', () => {
  // State
  const user = ref<User | null>(null)
  const claims = ref<UserCustomClaims | null>(null)
  const loading = ref(true)
  const error = ref<string | null>(null)
  const initialized = ref(false)

  // Getters
  const isAuthenticated = computed(() => !!user.value)
  const isSuperAdmin = computed(() => claims.value?.isSuperAdmin ?? false)
  const isPlatformSupport = computed(() => claims.value?.isPlatformSupport ?? false)
  const currentTenantId = computed(() => claims.value?.tenantId ?? null)
  const currentSubdomain = computed(() => claims.value?.tenantSubdomain ?? null)
  const currentRole = computed(() => claims.value?.tenantRole ?? null)
  const userEmail = computed(() => user.value?.email ?? null)
  const userDisplayName = computed(() => user.value?.displayName ?? null)
  const userPhotoURL = computed(() => user.value?.photoURL ?? null)
  const permissions = computed(() => claims.value?.permissions ?? [])

  /**
   * Check if user has a specific permission
   */
  function hasPermission(permission: string): boolean {
    const perms = permissions.value
    if (perms.includes('*')) return true
    if (perms.includes(permission)) return true

    const [action] = permission.split(':')
    return perms.includes(`${action}:*`)
  }

  /**
   * Sign in with email and password
   */
  async function signIn(email: string, password: string): Promise<void> {
    loading.value = true
    error.value = null
    try {
      const credential = await signInWithEmailAndPassword(auth, email, password)
      user.value = credential.user
      await refreshClaims()
    } catch (err) {
      error.value = getErrorMessage(err)
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * Sign in with Google OAuth
   * Note: Due to COOP browser policies, we don't await the popup result.
   * Instead, onAuthStateChanged will detect the sign-in.
   */
  async function signInWithGoogle(): Promise<void> {
    loading.value = true
    error.value = null
    try {
      const provider = new GoogleAuthProvider()
      provider.addScope('email')
      provider.addScope('profile')

      // Start popup but don't block on it due to COOP issues
      signInWithPopup(auth, provider)
        .then(async (credential) => {
          user.value = credential.user
          await refreshClaims()
          loading.value = false
        })
        .catch((err) => {
          // Only set error if user didn't cancel
          if ((err as { code?: string }).code !== 'auth/popup-closed-by-user') {
            error.value = getErrorMessage(err)
          }
          loading.value = false
        })

      // Return immediately - auth state listener will handle the rest
      // Give the popup time to open before returning
      await new Promise(resolve => setTimeout(resolve, 500))
    } catch (err) {
      error.value = getErrorMessage(err)
      loading.value = false
      throw err
    }
  }

  /**
   * Sign in with Microsoft OAuth
   * Note: Due to COOP browser policies, we don't await the popup result.
   * Instead, onAuthStateChanged will detect the sign-in.
   */
  async function signInWithMicrosoft(): Promise<void> {
    loading.value = true
    error.value = null
    try {
      const provider = new OAuthProvider('microsoft.com')
      provider.addScope('email')
      provider.addScope('profile')

      // Start popup but don't block on it due to COOP issues
      signInWithPopup(auth, provider)
        .then(async (credential) => {
          user.value = credential.user
          await refreshClaims()
          loading.value = false
        })
        .catch((err) => {
          // Only set error if user didn't cancel
          if ((err as { code?: string }).code !== 'auth/popup-closed-by-user') {
            error.value = getErrorMessage(err)
          }
          loading.value = false
        })

      // Return immediately - auth state listener will handle the rest
      // Give the popup time to open before returning
      await new Promise(resolve => setTimeout(resolve, 500))
    } catch (err) {
      error.value = getErrorMessage(err)
      loading.value = false
      throw err
    }
  }

  /**
   * Create new account with email and password
   */
  async function signUp(email: string, password: string): Promise<void> {
    loading.value = true
    error.value = null
    try {
      const credential = await createUserWithEmailAndPassword(auth, email, password)
      user.value = credential.user
      await refreshClaims()
    } catch (err) {
      error.value = getErrorMessage(err)
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * Sign out current user
   */
  async function signOut(): Promise<void> {
    loading.value = true
    try {
      await firebaseSignOut(auth)
      user.value = null
      claims.value = null
    } finally {
      loading.value = false
    }
  }

  /**
   * Refresh custom claims from token
   */
  async function refreshClaims(): Promise<void> {
    if (!user.value) {
      claims.value = null
      return
    }
    try {
      const token = await user.value.getIdTokenResult(true)
      claims.value = token.claims as unknown as UserCustomClaims
    } catch (err) {
      console.error('Failed to refresh claims:', err)
      claims.value = null
    }
  }

  /**
   * Convert Firebase error to user-friendly message
   */
  function getErrorMessage(err: unknown): string {
    if (err instanceof Error) {
      const code = (err as { code?: string }).code
      switch (code) {
        case 'auth/invalid-credential':
        case 'auth/wrong-password':
        case 'auth/user-not-found':
          return 'Invalid email or password'
        case 'auth/user-disabled':
          return 'This account has been disabled'
        case 'auth/too-many-requests':
          return 'Too many attempts. Please try again later.'
        case 'auth/email-already-in-use':
          return 'An account with this email already exists'
        case 'auth/weak-password':
          return 'Password should be at least 6 characters'
        case 'auth/invalid-email':
          return 'Please enter a valid email address'
        case 'auth/popup-closed-by-user':
          return 'Sign in was cancelled'
        default:
          return err.message
      }
    }
    return 'An unknown error occurred'
  }

  /**
   * Initialize auth state listener
   */
  function init(): Promise<void> {
    return new Promise((resolve) => {
      if (initialized.value) {
        resolve()
        return
      }

      const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
        user.value = firebaseUser
        if (firebaseUser) {
          await refreshClaims()
        } else {
          claims.value = null
        }
        loading.value = false
        initialized.value = true
        resolve()
      })

      // Store unsubscribe function for cleanup if needed
      return unsubscribe
    })
  }

  return {
    // State
    user,
    claims,
    loading,
    error,
    initialized,
    // Getters
    isAuthenticated,
    isSuperAdmin,
    isPlatformSupport,
    currentTenantId,
    currentSubdomain,
    currentRole,
    userEmail,
    userDisplayName,
    userPhotoURL,
    permissions,
    // Actions
    hasPermission,
    signIn,
    signInWithGoogle,
    signInWithMicrosoft,
    signUp,
    signOut,
    refreshClaims,
    init
  }
})
